import React, { useContext, useRef, useState } from "react";
import { Form, Formik } from "formik";
import TextBox from "../common/TextBox";
import Button from "../common/Button";
import { langComponent } from "../../constants";
import AppContext from "../../AppContext";

import { ReactComponent as ArrowRightSvg } from "../../assets/icons/arrow-right.svg";
import { ReactComponent as ArrowLeftSvg } from "../../assets/icons/arrow-left.svg";
import { ReactComponent as VisaSvg } from "../../assets/payment/visa.svg";
import { ReactComponent as MasterCardSvg } from "../../assets/payment/mastercard.svg";
import { ReactComponent as UnionPaySvg } from "../../assets/payment/unionpay.svg";
import { ReactComponent as JcbSvg } from "../../assets/payment/jcb.svg";
import _ from "lodash";
import { format, startOfMonth } from "date-fns";
import ComboBox from "../common/ComboBox";
import Overlay from "../Overlay";
import useUsers from "../../hooks/useUsers";
import useOmise from "../../hooks/useOmise";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const monthOptions = _.range(1, 13).map((_, month) => ({
  name: format(new Date(1900, month, 1), "MMMM"),
  value: (month + 1).toString(),
}));

const TrialUpgrade = () => {
  const [step, setStep] = useState(1);
  const { user, getLang, setIsTrialEnded } = useContext(AppContext);
  const TranslationCommon = getLang(langComponent.common);
  const TranslationProfile = getLang(langComponent.profile);
  const { patchPaymentMethod, patchUser } = useUsers();
  const { getCard, addCard, deleteCard } = useOmise();
  const navigate = useNavigate();

  const handleValidate = ({ card, month, year, cvc, name }) => {
    let errors = {};

    if (!card) errors.card = TranslationCommon("Required");
    if (!month) errors.month = TranslationCommon("Required");

    if (!year) errors.year = TranslationCommon("Required");
    if (!cvc) errors.cvc = TranslationCommon("Required");
    if (!name) errors.name = TranslationCommon("Required");

    if (year && year.length != 2) errors.year = TranslationCommon("Required");
    if (cvc && cvc.length != 3) errors.cvc = TranslationCommon("Required");

    // * Check expiration date
    if (month && year) {
      const current = startOfMonth(new Date());
      const exp = startOfMonth(new Date(+`20${year}`, +month - 1, 1));

      if (exp < current) errors.year = TranslationCommon("Expired");
    }

    console.log("errors", errors);

    return errors;
  };

  const handleSubmit = (
    { card, month, year, cvc, name },
    { setSubmitting }
  ) => {
    // debugger;
    setSubmitting(true);
    Omise.createToken(
      "card",
      {
        expiration_month: +month,
        expiration_year: +`20${year}`,
        number: card,
        security_code: cvc,
        name,
      },
      async (_statusCode, response) => {
        if (!response || !response.card) {
          toast.error("Unable to create card");
          setSubmitting(false);
          console.error(response);
          return;
        }

        const cardToken = response.id;

        try {
          await patchPaymentMethod(user._id, cardToken);
          await patchUser(user._id, { trialEndOn: null });
          setIsTrialEnded(false);

          toast.success("Updated payment method");
          return;
        } catch (error) {
          toast.error("Failed to update payment method");
          console.error("Failed to update card:", error);
        } finally {
          setSubmitting(false);
        }
      }
    );
  };

  const _getCardType = (cardNumber) => {
    // Remove all non-digit characters from the input
    const sanitized = cardNumber.replace(/\D/g, "");

    // Regex patterns for various card types
    const cardPatterns = {
      visa: /^4\d{12}(\d{3})?$/,
      mastercard:
        /^(5[1-5]\d{4}|222[1-9]|22[3-9]\d{3}|2[3-6]\d{4}|27[01]\d{2}|2720)\d{10}$/,
      jcb: /^(?:2131|1800|35\d{3})\d{11}$/,
      unionpay: /^(62\d{14,17}|6[5-9]\d{13,18})$/,
    };

    // Check the card number against each pattern
    for (const [cardType, pattern] of Object.entries(cardPatterns)) {
      if (pattern.test(sanitized)) {
        return cardType;
      }
    }

    // If no pattern matches, return 'unknown'
    return "unknown";
  };

  return (
    <Overlay>
      <div className="trial-ended">
        {step === 1 && (
          <>
            <h4>Your Trial Has Ended</h4>
            <p>
              Your 3 month trial has ended. Upgrade now to continue using the
              current plan. Or select another plan
            </p>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <Button label={"Upgrade"} onClick={() => setStep(2)} />
              <Button
                label={"Switch to another plan"}
                variant="tertiary"
                onClick={() => navigate("/change-plan")}
              />
            </div>
          </>
        )}
        {step === 2 && (
          <>
            <Formik
              initialValues={{
                card: "4242424242424242",
                cvc: "123",
                name: "Card Test",
                year: "30",
                month: "4",
              }}
              validate={handleValidate}
              onSubmit={handleSubmit}
            >
              {({ values }) => {
                const _cardType = _getCardType(values.card);

                const getCardTypeClassname = (type) => {
                  if (_cardType === type) {
                    return "cart-content-payment-form-card-types__icon cart-content-payment-form-card-types__icon--active";
                  }

                  return "cart-content-payment-form-card-types__icon";
                };

                return (
                  <Form>
                    <h4>Enter Your Payment Infomation</h4>
                    <div className="onboard__form-content">
                      <div className="onboard__form-item">
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            fontSize: 14,
                            color: "#333",
                            margin: "0 8px",
                          }}
                        >
                          Card no
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            <VisaSvg
                              className={getCardTypeClassname("visa")}
                              height={20}
                              width={20}
                            />
                            <MasterCardSvg
                              className={getCardTypeClassname("mastercard")}
                              height={20}
                              width={20}
                            />
                            <JcbSvg
                              className={getCardTypeClassname("jcb")}
                              height={20}
                              width={20}
                            />
                            <UnionPaySvg
                              className={getCardTypeClassname("unionpay")}
                              height={20}
                              width={20}
                            />
                          </div>
                        </div>
                        <TextBox
                          name="card"
                          mask="9999 9999 9999 9999"
                          maskPlaceholder=""
                          placeholder="0000 0000 0000 0000"
                        />
                      </div>
                      <div className="onboard__form-item">
                        <div className="onboard__form-carddetail">
                          <ComboBox
                            name={"month"}
                            placeholder={"Month"}
                            options={monthOptions}
                            label={"Expiration"}
                          />
                          <TextBox
                            name="year"
                            placeholder={"YY"}
                            label={"Year"}
                            hideLimit
                            mask="99"
                            maskPlaceholder=""
                            autoComplete="off"
                          />
                          <TextBox
                            name="cvc"
                            placeholder={"***"}
                            label={"CVC"}
                            hideLimit
                            mask="999"
                            maskPlaceholder=""
                            autoComplete="off"
                          />
                        </div>
                      </div>
                      <div className="onboard__form-item">
                        <TextBox name="name" label={"Name on card"} />
                      </div>
                    </div>
                    <div className="onboard__form-cta">
                      <Button
                        label={TranslationCommon("Return")}
                        leadingIcon={<ArrowLeftSvg />}
                        onClick={() => setStep(1)}
                        variant="secondary"
                      />
                      <div className="onboard__form-cta__divider" />
                      <Button
                        type="submit"
                        label={TranslationCommon("Continue")}
                        trailingIcon={<ArrowRightSvg />}
                      />
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </>
        )}
      </div>
    </Overlay>
  );
};

TrialUpgrade.propTypes = {};

export default TrialUpgrade;
